import classNames from "classnames";
import React, { useCallback, forwardRef, PropsWithChildren } from "react";
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import { Controller, FieldPath, FieldPathValue, FieldValues, Control } from "react-hook-form";
import { Input, InputProps } from "Components/ReactstrapSlim";
import * as Icons from "Components/Icons";
import { createPortal } from "react-dom";
import useMediaBreakpointDown from "Hooks/useMediaBreakpointDown";
import moment from "moment";
import { toDate, toISOStringUnspecified } from "Utils/date";

interface DatePickerCustomInputProps extends InputProps {
   errorMessage?: string;
   useFloatLabel?: boolean;
   wrapperClasses: string;
   withoutIcon?: boolean;
}

const DatePickerCustomInput = forwardRef<HTMLInputElement, DatePickerCustomInputProps>(({ name, errorMessage, value, useFloatLabel, withoutIcon, onClick, onChange, onKeyDown, placeholder, wrapperClasses, bsSize, ...rest }, ref) => {
   const hasInvalidMessage = !!errorMessage;
   return <div className={wrapperClasses} >
      <Input
         innerRef={ref}
         onChange={onChange}
         onClick={onClick}
         onKeyDown={onKeyDown}
         value={value}
         bsSize={bsSize}
         className="rfl-input rfl-input--hasicon"
         aria-label="date"
         placeholder={useFloatLabel ? undefined : placeholder}
         {...rest} />
      {useFloatLabel && !hasInvalidMessage && <label htmlFor={name} className="rfl-label">
         {placeholder}
      </label>}
      {!withoutIcon && <div className="rfl-icon rfl-icon--pntr-none" >
         <Icons.Calendar />
      </div>}
      {useFloatLabel && hasInvalidMessage && <div role="alert" className="rfl-valdation-message">
         {errorMessage}
      </div>}
   </div>
});

export interface DatePickerInputProps extends ReactDatePickerProps {
   bsSize?: InputProps["bsSize"];
   elementToAttach?: HTMLElement | undefined;
   errorMessage?: string;
   isInvalid?: boolean;
   name: string;
   useFloatLabel?: boolean;
   withoutIcon?: boolean;
}
export const DatePickerInput: React.FC<DatePickerInputProps> = ({ bsSize, elementToAttach, errorMessage, isInvalid, name, useFloatLabel, withoutIcon, className, onCalendarOpen, onCalendarClose, onFocus, onBlur, popperClassName, ...rest }) => {
   const withPortal = useMediaBreakpointDown("sm");
   const wrapperClasses = classNames(
      "rfl",
      "rfl-datepicker",
      {
         "rfl-datepicker-nofl": !useFloatLabel,
         "rfl--invalid": isInvalid,
         "rfl--hasvalue": !!rest.selected
      },
      bsSize ? `rfl-${bsSize}` : null,
      className);

   const onFocusWrapper = (e: React.FocusEvent<HTMLInputElement>) => {
      if (withPortal)
         e.target.readOnly = true;
      if (onFocus)
         onFocus(e)
   }

   const onBlurWrapper = (e: React.FocusEvent<HTMLInputElement>) => {
      if (withPortal)
         e.target.readOnly = false;
      if (onBlur)
         onBlur(e)
   }

   const handleTouchStart = (e) => e.stopPropagation();
   const onCalendarOpenWrapped = () => {
      if (withPortal) {
         document.addEventListener("touchstart", handleTouchStart, true);
      }
      if (onCalendarOpen)
         onCalendarOpen()
   };
   const onCalendarCloseWrapped = () => {
      if (withPortal) {
         document.removeEventListener("touchstart", handleTouchStart, true);
      }
      if (onCalendarClose)
         onCalendarClose();
   }

   const DatePickerPortal = useCallback(({ children }) => {
      return elementToAttach ? createPortal(children, elementToAttach) : null;
   }, [elementToAttach]);

   return (<DatePicker
      {...rest}
      popperContainer={elementToAttach ? DatePickerPortal : undefined}
      popperClassName={classNames("rfl-datepicker-popper", popperClassName)}
      showPopperArrow={false}
      customInput={<DatePickerCustomInput
         name={name}
         errorMessage={errorMessage}
         placeholder={rest.placeholderText}
         wrapperClasses={wrapperClasses}
         bsSize={bsSize}
         disabled={rest.disabled}
         useFloatLabel={useFloatLabel}
         withoutIcon={withoutIcon} />}
      formatWeekDay={nameOfDay => nameOfDay.substring(0, 1)}
      withPortal={withPortal}
      portalId={withPortal ? "datepicker-portal" : undefined}
      onCalendarOpen={onCalendarOpenWrapped}
      onCalendarClose={onCalendarCloseWrapped}
      onFocus={onFocusWrapper}
      onBlur={onBlurWrapper}
   />)
}

export interface DatePickerInputControlledProps<T extends FieldValues> extends Omit<DatePickerInputProps, "errorMessage" | "isInvalid" | "selected" | "onChange" | "name"> {
   control: Control<T>,
   name: FieldPath<T>,
   doNotAdjustToLocalTimeZone?: boolean
}

export const DatePickerInputControlled = <T extends FieldValues,>({ control, name, ...rest }: PropsWithChildren<DatePickerInputControlledProps<T>>) => {
   return (<Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
         if (moment.isMoment(field.value)) {
            console.error(`moment is deprecated for form fields (${name})`);
         }
         return <DatePickerInput
            {...rest}
            errorMessage={fieldState.error?.message}
            isInvalid={!!fieldState.error}
            name={name}
            selected={field.value}
            onChange={value => field.onChange(value as FieldPathValue<T, typeof name>)}
         />
      }
      }
   />)
}

const parseToDate = (ISODate: string, doNotAdjustToLocalTimeZone = false): Date => {
   const date = toDate(ISODate);   
   if(doNotAdjustToLocalTimeZone) {
      const userTimezoneOffset = date.getTimezoneOffset() * 60000;
      date.setMilliseconds(date.getMilliseconds() + userTimezoneOffset)
   }

   return date;
}

const parseToISO = (date: Date, doNotAdjustToLocalTimeZone = false): string => {
   if(doNotAdjustToLocalTimeZone) {
      const userTimezoneOffset = date.getTimezoneOffset() * 60000;
      date.setMilliseconds(date.getMilliseconds() - userTimezoneOffset)
   }

   return date.toISOString();
}

export const DatePickerInputControlledForString = <T extends FieldValues,>({ control, name, doNotAdjustToLocalTimeZone, ...rest }: PropsWithChildren<DatePickerInputControlledProps<T>>) => {
   return (<Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
         return (
            <DatePickerInput
               {...rest}
               errorMessage={fieldState.error?.message}
               isInvalid={!!fieldState.error}
               name={name}
               selected={field.value && parseToDate(field.value, doNotAdjustToLocalTimeZone)}
               onChange={date => {
                  const d = Array.isArray(date) ? date[0] : date;
                  field.onChange(d && parseToISO(d, doNotAdjustToLocalTimeZone))
               }}
            />
         )
      }}
   />)
}

//TODO: Make it the only DatePickerInputControlled
export const DatePickerInputControlledForLocalString = <T extends FieldValues,>({ control, name, doNotAdjustToLocalTimeZone, ...rest }: PropsWithChildren<DatePickerInputControlledProps<T>>) => {
   return (<Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
         return (
            <DatePickerInput
               {...rest}
               errorMessage={fieldState.error?.message}
               isInvalid={!!fieldState.error}
               name={name}
               selected={toDate(field.value)}
               onChange={date => {
                  const d = Array.isArray(date) ? date[0] : date;                  
                  field.onChange(d ? toISOStringUnspecified(d) : d);
               }}
            />
         )
      }}
   />)
}

